import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useMemo } from 'react';
import type { AllEntities, BaseEntity } from 'c-sdk';
import type { SearchPayload } from '../Types/Search';
import { getBaseSliceSelectors, getBaseSliceThunks } from '../Data/EntitySchemaActions';
import type { CustomListSearchGenerator, EntityStores, SlicePaginationData } from '../Types';
import { hookUtilGetPaginatedList } from './utils';
import { paginatedDataToSearchPayload } from '../lib';

// One shared empty map: a fresh `{}` per call makes useSelector see a new result every time
// (react-redux's stability warning, and a re-render on every store update).
const noRows = {};

const usePaginatedEntityData = <RootState, Entity extends BaseEntity>(
    tag: string,
    entityName: keyof AllEntities,
    customSearch?: CustomListSearchGenerator,
) => {
    const dispatch = useDispatch();

    const thunks = useMemo(() => getBaseSliceThunks<RootState, Entity>(entityName), [entityName]);
    const selectors = useMemo(() => getBaseSliceSelectors<Entity>(entityName), [entityName]);

    const paginatedData = useSelector((state: EntityStores<Entity>) =>
        selectors.paginationData(state, tag),
    ) as unknown as SlicePaginationData<Entity>;

    const paginatedRows = useSelector((state: EntityStores<Entity>) => {
        const key = entityName.charAt(0).toLowerCase() + entityName.slice(1);
        return state[key]?.entities ?? noRows;
    });

    const currentMetaPage = useMemo(
        () => paginatedData?.meta?.pagination?.current_page,
        [paginatedData],
    );

    const getPaginationData = useCallback(
        (searchPayload: SearchPayload<Entity>, forceRefresh = false) => {
            hookUtilGetPaginatedList(thunks.getPaginatedList)(
                tag,
                forceRefresh,
                searchPayload,
                dispatch,
                false,
                customSearch,
                currentMetaPage,
            );
        },
        [tag, thunks, dispatch, customSearch, currentMetaPage],
    );
    const existingFilters = useMemo(() => paginatedData?.meta?.filters ?? {}, [paginatedData]);

    const refresh = useCallback(
        (searchOverrides?: Partial<SearchPayload<Entity>>) => {
            if (paginatedData != null) {
                getPaginationData(
                    paginatedDataToSearchPayload(paginatedData, searchOverrides),
                    true,
                );
            }
        },
        [paginatedData, getPaginationData],
    );

    const upsert = useCallback(
        (entity: Entity) => {
            dispatch(thunks.upsertEntity(entity));
        },
        [dispatch, thunks],
    );

    const upsertMany = useCallback(
        (entities: Entity[]) => {
            dispatch(thunks.upsertEntities(entities));
        },
        [dispatch, thunks],
    );

    const reset = useCallback(() => {
        dispatch(thunks.resetPaginatedList(tag));
    }, [dispatch, tag, thunks]);

    return useMemo(
        () => ({
            paginatedRows,
            paginatedData,
            getPaginationData,
            refresh,
            reset,
            thunks,
            upsert,
            upsertMany,
            existingFilters,
        }),
        [
            paginatedData,
            getPaginationData,
            refresh,
            reset,
            thunks,
            upsert,
            upsertMany,
            paginatedRows,
            existingFilters,
        ],
    );
};

export default usePaginatedEntityData;
